import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core";
import {
  emailMessages,
  lessonWaitlistOffers,
  organisations,
  students,
  users,
} from "./schemaBase.js";
import { concertInvitations } from "./concerts.js";

export const retentionFollowUpCategories = [
  "taster",
  "cancellation",
  "unused_credit",
] as const;
export type RetentionFollowUpCategory =
  (typeof retentionFollowUpCategories)[number];

export const optionalEmailNoticeCategories = [
  "short_notice_offer",
  "retention_follow_up",
  "weekly_summary",
  "concert_invitation",
] as const;
export type OptionalEmailNoticeCategory =
  (typeof optionalEmailNoticeCategories)[number];

export type OptionalEmailNoticeInput =
  | {
      category: "short_notice_offer";
      studentId: string;
      offerId: string;
    }
  | {
      category: "retention_follow_up";
      studentId: string;
      retentionCategory: RetentionFollowUpCategory;
      evidenceAt: string;
    }
  | {
      category: "weekly_summary";
      studentId: string;
    }
  | {
      category: "concert_invitation";
      studentId: string;
      invitationId: string;
    };

/**
 * Learner-scoped short-notice offer choice and quiet hours. Practice bells, weekly summaries, and optional concert invitation emails use
 * familyAccountNotificationPreferences so each linked account can choose
 * separately for each learner.
 */
export const familyNotificationPreferences = pgTable(
  "family_notification_preferences",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    // A waitlist request is the family's explicit request for an opening.
    // True preserves the existing email behavior until they turn it off.
    shortNoticeOffersEnabled: boolean("short_notice_offers_enabled")
      .notNull()
      .default(true),
    quietHoursStartMinute: integer("quiet_hours_start_minute"),
    quietHoursEndMinute: integer("quiet_hours_end_minute"),
    updatedByUserId: varchar("updated_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("family_notification_preferences_student_idx").on(
      t.organisationId,
      t.studentId,
    ),
    check(
      "family_notification_preferences_quiet_pair_check",
      sql.raw(
        "(quiet_hours_start_minute is null) = (quiet_hours_end_minute is null)",
      ),
    ),
    check(
      "family_notification_preferences_quiet_range_check",
      sql.raw(
        "(quiet_hours_start_minute is null or (quiet_hours_start_minute between 0 and 1439 and quiet_hours_end_minute between 0 and 1439 and quiet_hours_start_minute <> quiet_hours_end_minute))",
      ),
    ),
  ],
);



/**
 * Account-scoped optional notices are subscribed to by the account receiving
 * them. The learner scope keeps siblings separate; the account scope prevents
 * one guardian's choice from opting another linked account in.
 */
export const familyAccountNotificationPreferences = pgTable(
  "family_account_notification_preferences",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    accountId: varchar("account_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    practiceAssignmentBellEnabled: boolean("practice_assignment_bell_enabled")
      .notNull()
      .default(false),
    weeklySummaryEnabled: boolean("weekly_summary_enabled")
      .notNull()
      .default(false),
    concertInvitationEmailEnabled: boolean("concert_invitation_email_enabled")
      .notNull()
      .default(false),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("family_account_notification_preferences_scope_idx").on(
      t.organisationId,
      t.studentId,
      t.accountId,
    ),
  ],
);

/**
 * Guardian-specific opt-in for optional R03 family follow-ups. A verified
 * parent account is the recipient; learner accounts never receive these emails.
 */
export const familyRetentionFollowUpPreferences = pgTable(
  "family_retention_follow_up_preferences",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    guardianUserId: varchar("guardian_user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    enabled: boolean("enabled").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("family_retention_follow_up_preferences_guardian_idx").on(
      t.organisationId,
      t.studentId,
      t.guardianUserId,
    ),
  ],
);

/** One successful queue per learner in any rolling 30-day window. */
export const retentionFollowUpCadence = pgTable(
  "retention_follow_up_cadence",
  {
    id: varchar("id").primaryKey().default(sql.raw("gen_random_uuid()")),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    studentId: varchar("student_id")
      .notNull()
      .references(() => students.id, { onDelete: "cascade" }),
    lastQueuedAt: timestamp("last_queued_at", { withTimezone: true }).notNull(),
    lastCategory: text("last_category")
      .$type<RetentionFollowUpCategory>()
      .notNull(),
    lastEvidenceAt: timestamp("last_evidence_at", { withTimezone: true }).notNull(),
    lastQueuedByUserId: varchar("last_queued_by_user_id").references(
      () => users.id,
      { onDelete: "set null" },
    ),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    uniqueIndex("retention_follow_up_cadence_student_idx").on(
      t.organisationId,
      t.studentId,
    ),
    check(
      "retention_follow_up_cadence_category_check",
      sql.raw("last_category in ('taster', 'cancellation', 'unused_credit')"),
    ),
  ],
);

/**
 * Only truly optional messages get this row. Keeping the metadata beside the
 * ordinary email outbox makes the normal runner the sole dispatcher while
 * allowing it to recheck the event, relationship and preference at claim time.
 *
 * Context references become null when their source is deleted. That preserves
 * the optional classification so a queued email is suppressed instead of
 * becoming an unclassified send.
 */
export const optionalEmailNotices = pgTable(
  "optional_email_notices",
  {
    emailMessageId: varchar("email_message_id")
      .primaryKey()
      .references(() => emailMessages.id, { onDelete: "cascade" }),
    organisationId: varchar("organisation_id")
      .notNull()
      .references(() => organisations.id, { onDelete: "cascade" }),
    category: text("category").$type<OptionalEmailNoticeCategory>().notNull(),
    studentId: varchar("student_id").references(() => students.id, {
      onDelete: "set null",
    }),
    offerId: varchar("offer_id").references(() => lessonWaitlistOffers.id, {
      onDelete: "set null",
    }),
    concertInvitationId: varchar("concert_invitation_id").references(() => concertInvitations.id, {
      onDelete: "set null",
    }),
    retentionCategory: text("retention_category").$type<RetentionFollowUpCategory>(),
    retentionEvidenceAt: timestamp("retention_evidence_at", { withTimezone: true }),
    quietUntil: timestamp("quiet_until", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (t) => [
    check(
      "optional_email_notices_category_check",
      sql.raw("category in ('short_notice_offer', 'retention_follow_up', 'weekly_summary', 'concert_invitation')"),
    ),
    index("optional_email_notices_learner_idx").on(
      t.organisationId,
      t.studentId,
      t.category,
    ),
    index("optional_email_notices_offer_idx").on(t.offerId),
    index("optional_email_notices_retention_evidence_idx").on(
      t.organisationId,
      t.studentId,
      t.retentionCategory,
      t.retentionEvidenceAt,
    ),
    check(
      "optional_email_notices_context_check",
      sql.raw(
        "(category = 'short_notice_offer' and retention_category is null and retention_evidence_at is null and concert_invitation_id is null) or (category = 'retention_follow_up' and offer_id is null and concert_invitation_id is null and retention_category in ('taster', 'cancellation', 'unused_credit') and retention_evidence_at is not null) or (category = 'weekly_summary' and offer_id is null and retention_category is null and retention_evidence_at is null and concert_invitation_id is null) or (category = 'concert_invitation' and student_id is not null and offer_id is null and retention_category is null and retention_evidence_at is null)",
      ),
    ),
  ],
);
